import React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import TopicsPage from './pages/TopicsPage';
import ChatPage from './pages/ChatPage';
import AuthPage from './pages/AuthPage';
import MiniAppDemoPage from './pages/MiniAppDemoPage';
import SettingsPage from './pages/SettingsPage';
import LaunchChecklistPage from './pages/LaunchChecklistPage';
import LessonPage from './pages/LessonPage';
import LessonCatalogPage from './pages/LessonCatalogPage';
import AboutPage from './pages/AboutPage';
import StoriesHomePage from './pages/stories/StoriesHomePage';
import StoriesCharacterPage from './pages/stories/StoriesCharacterPage';
import StoryPlayerPage from './pages/stories/StoryPlayerPage';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Sidebar from './components/Sidebar';
import { initializeAnalytics, trackPageView } from './utils/analytics';
import { derivePageTitle } from './utils/pageTitles';

// Component to track page views with human-readable titles (GA4 Pages and Screens)
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    const pageTitle = derivePageTitle(location.pathname);
    document.title = pageTitle;
    trackPageView(location.pathname, pageTitle);
  }, [location.pathname]);

  return null;
}

/**
 * Guards /settings so it can only be reached after a successful re-auth via
 * the Sidebar's "Parent verification" modal (which sets the session flag).
 * A 10-minute TTL covers normal browsing within the settings page without
 * requiring a fresh verify on every internal navigation.
 */
const SETTINGS_REAUTH_KEY = 'explore_settings_reauth';
const SETTINGS_REAUTH_TTL = 10 * 60 * 1000;

function ProtectedSettingsRoute() {
  const ts = sessionStorage.getItem(SETTINGS_REAUTH_KEY);
  const valid = ts && Date.now() - Number(ts) < SETTINGS_REAUTH_TTL;
  if (!valid) return <Navigate to="/" replace />;
  return <SettingsPage />;
}

/** Old bookmark `/topics/catalog` → lesson index at `/topics` (or `/:locale/topics`). */
function RedirectTopicsCatalogToIndex() {
  const { locale } = useParams();
  return <Navigate to={locale ? `/${locale}/topics` : '/topics'} replace />;
}

function LegacySearchRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname.startsWith('/search-result.html') || location.pathname.startsWith('/search-result/')) {
      const params = new URLSearchParams(location.search);
      const q = params.get('q');
      if (q) {
        navigate(`/s?q=${encodeURIComponent(q)}`, { replace: true });
      }
    }
  }, [location, navigate]);

  return null;
}

function AppShell() {
  const location = useLocation();
  const hideSidebar =
    location.pathname.includes('/lesson/') ||
    (location.pathname.startsWith('/stories') && location.pathname.split('/').length >= 4);

  return (
    <>
      <LegacySearchRedirect />
      <PageViewTracker />
      <div
        className="App"
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100dvh',
          background: 'var(--color-bg)',
        }}
      >
        {!hideSidebar ? <Sidebar /> : null}
        <div
          style={{
            flex: 1,
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: 0,
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
            paddingBottom: hideSidebar ? 0 : 'calc(56px + env(safe-area-inset-bottom, 0px))',
            boxSizing: 'border-box',
          }}
          className="main-content"
        >
          <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/:locale/about" element={<AboutPage />} />
              <Route path="/search-result.html" element={<HomePage />} />
              <Route path="/search-result/" element={<HomePage />} />
              <Route path="/s/*" element={<SearchPage searchType="web" />} />
              <Route path="/g" element={<SearchPage searchType="game" />} />
              <Route path="/e" element={<SearchPage searchType="education" />} />
              <Route path="/f" element={<SearchPage searchType="facts" />} />
              <Route path="/i" element={<SearchPage searchType="images" />} />
              <Route path="/n" element={<SearchPage searchType="news" />} />
              <Route path="/v" element={<SearchPage searchType="videos" />} />
              {/* Localized routes for internationalization - handles any locale */}
              <Route path="/:locale/s/*" element={<SearchPage searchType="web" />} />
              <Route path="/:locale/g" element={<SearchPage searchType="game" />} />
              <Route path="/:locale/e" element={<SearchPage searchType="education" />} />
              <Route path="/:locale/f" element={<SearchPage searchType="facts" />} />
              <Route path="/:locale/i" element={<SearchPage searchType="images" />} />
              <Route path="/:locale/n" element={<SearchPage searchType="news" />} />
              <Route path="/:locale/v" element={<SearchPage searchType="videos" />} />
              <Route path="/topics/catalog" element={<Navigate to="/topics" replace />} />
              <Route path="/topics/:worldId/lesson/:lessonId" element={<LessonPage />} />
              <Route path="/topics/explorer" element={<TopicsPage />} />
              <Route path="/topics/:worldId" element={<TopicsPage />} />
              <Route path="/topics" element={<LessonCatalogPage />} />
              <Route path="/:locale/topics/catalog" element={<RedirectTopicsCatalogToIndex />} />
              <Route path="/:locale/topics/:worldId/lesson/:lessonId" element={<LessonPage />} />
              <Route path="/:locale/topics/explorer" element={<TopicsPage />} />
              <Route path="/:locale/topics/:worldId" element={<TopicsPage />} />
              <Route path="/:locale/topics" element={<LessonCatalogPage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/settings" element={<ProtectedSettingsRoute />} />
              <Route path="/miniapps" element={<MiniAppDemoPage />} />
              <Route path="/launch-checklist" element={<LaunchChecklistPage />} />
              <Route path="/:locale/launch-checklist" element={<LaunchChecklistPage />} />
              <Route path="/restricted" element={<RestrictedPage />} />
              <Route path="/stories" element={<StoriesHomePage />} />
              <Route path="/stories/:character" element={<StoriesCharacterPage />} />
              <Route path="/stories/:character/:storyId" element={<StoryPlayerPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </div>
      </div>

    </>
  );
}

function App() {
  useEffect(() => {
    initializeAnalytics();
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </ThemeProvider>
  );
}

// Simple restricted page component
function RestrictedPage() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      textAlign: 'center',
      padding: '20px'
    }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>🚫</div>
      <h1 style={{ color: 'var(--color-text)', marginBottom: 16 }}>Access Restricted</h1>
      <p style={{ color: 'var(--color-text)', marginBottom: 24, opacity: 0.9 }}>
        This content is not available for your age group or region.
      </p>
      <a href="/" className="btn">
        ← Back to Home
      </a>
    </div>
  );
}

export default App; 